/**
 * GLDN Cart Unique Property
 *
 * Injects a per-add `_unique` line-item property on every add-to-cart
 * regardless of which JS path triggered it (PDP form submit, quick-add
 * button, recommendations "Add Again", personalizer's own /cart/add.js,
 * etc.). Matches live behavior — without `_unique`, Shopify merges
 * identical-property adds into a quantity bump instead of separate
 * cart lines, breaking the "same product to different gift recipients"
 * flow.
 *
 * Implementation: monkey-patch window.fetch so every POST to
 * /cart/add.js has `_unique` added to its body before the request goes
 * out. Covers all paths without needing to enumerate trigger sources.
 *
 * The personalizer separately injects `_random_id` on builder items —
 * we leave those alone (don't double-inject when _random_id is already
 * present in the body).
 */
(function () {
  if (!window.fetch) return;

  function uniqueValue() {
    // 12-char base36 string: timestamp ms + random tail. Enough collision
    // resistance for a single session; fresh on every add.
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function isCartAddURL(url) {
    if (!url) return false;
    if (typeof url !== 'string') {
      try { url = url.url || String(url); } catch (e) { return false; }
    }
    // Match /cart/add and /cart/add.js, with or without query string,
    // on the current origin or a relative path.
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  function addUniqueToFormData(formData) {
    if (formData.has('properties[_unique]')) return formData;
    if (formData.has('properties[_random_id]')) return formData;
    formData.append('properties[_unique]', uniqueValue());
    return formData;
  }

  function addUniqueToJson(jsonBody) {
    if (!jsonBody || typeof jsonBody !== 'object') return jsonBody;
    // /cart/add.js JSON form: { id, quantity, properties: {...} }
    // or shorthand: { items: [{ id, quantity, properties: {...} }, ...] }
    function ensureProps(item) {
      if (!item || typeof item !== 'object') return;
      item.properties = item.properties || {};
      if (!Object.prototype.hasOwnProperty.call(item.properties, '_unique') &&
          !Object.prototype.hasOwnProperty.call(item.properties, '_random_id')) {
        item.properties._unique = uniqueValue();
      }
    }
    if (Array.isArray(jsonBody.items)) {
      jsonBody.items.forEach(ensureProps);
    } else if ('id' in jsonBody) {
      ensureProps(jsonBody);
    }
    return jsonBody;
  }

  function addUniqueToUrlEncoded(str) {
    if (typeof str !== 'string') return str;
    if (str.indexOf('properties[_unique]') !== -1) return str;
    if (str.indexOf('properties[_random_id]') !== -1) return str;
    var sep = str.length === 0 ? '' : '&';
    return str + sep + 'properties[_unique]=' + encodeURIComponent(uniqueValue());
  }

  function mutateBody(body, contentType) {
    /* Returns the (possibly new) body value to send. */
    if (body == null) return body;
    if (body instanceof FormData) {
      addUniqueToFormData(body);
      return body;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (!body.has('properties[_unique]') && !body.has('properties[_random_id]')) {
        body.append('properties[_unique]', uniqueValue());
      }
      return body;
    }
    if (typeof body === 'string') {
      var isJson = (contentType && contentType.indexOf('application/json') !== -1) ||
                   body.charAt(0) === '{' || body.charAt(0) === '[';
      if (isJson) {
        try {
          var parsed = JSON.parse(body);
          addUniqueToJson(parsed);
          return JSON.stringify(parsed);
        } catch (e) {
          return addUniqueToUrlEncoded(body);
        }
      }
      return addUniqueToUrlEncoded(body);
    }
    return body;
  }

  /* ---- fetch interception ---- */
  var originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (!isCartAddURL(url)) return originalFetch(input, init);
      var method = (init && init.method) || (input && input.method) || 'GET';
      if (String(method).toUpperCase() !== 'POST') return originalFetch(input, init);

      if (init && init.body != null) {
        var contentType = '';
        if (init.headers) {
          try {
            if (typeof init.headers.get === 'function') {
              contentType = init.headers.get('Content-Type') || init.headers.get('content-type') || '';
            } else {
              contentType = init.headers['Content-Type'] || init.headers['content-type'] || '';
            }
          } catch (e) {}
        }
        init.body = mutateBody(init.body, contentType);
      }
    } catch (e) {
      try { console.warn('[gldn-cart-unique] fetch inject failed, passing through:', e); } catch (_) {}
    }
    return originalFetch(input, init);
  };

  /* ---- XMLHttpRequest interception ----
     jQuery $.ajax (used by the GLDN personalizer's /cart/add.js call)
     goes through XHR, not fetch. Track the request URL + method on
     open() and rewrite the body on send() when it's a /cart/add POST. */
  if (window.XMLHttpRequest) {
    var XHR = window.XMLHttpRequest.prototype;
    var origOpen = XHR.open;
    var origSend = XHR.send;
    var origSetRequestHeader = XHR.setRequestHeader;

    XHR.open = function (method, url) {
      try {
        this._gldnUniqueMethod = method;
        this._gldnUniqueUrl = url;
        this._gldnUniqueContentType = '';
      } catch (e) {}
      return origOpen.apply(this, arguments);
    };

    XHR.setRequestHeader = function (name, value) {
      try {
        if (name && String(name).toLowerCase() === 'content-type') {
          this._gldnUniqueContentType = String(value || '');
        }
      } catch (e) {}
      return origSetRequestHeader.apply(this, arguments);
    };

    XHR.send = function (body) {
      try {
        var method = this._gldnUniqueMethod;
        var url = this._gldnUniqueUrl;
        if (method && String(method).toUpperCase() === 'POST' && isCartAddURL(url)) {
          var mutated = mutateBody(body, this._gldnUniqueContentType);
          if (mutated !== body) {
            return origSend.call(this, mutated);
          }
        }
      } catch (e) {
        try { console.warn('[gldn-cart-unique] xhr inject failed, passing through:', e); } catch (_) {}
      }
      return origSend.apply(this, arguments);
    };
  }
})();
